import { ThemeOptions } from '@mui/material/styles';
import { radii, lightColors, brand, iconBadgeTones, buildShadows } from './tokens';
import { buildComponents } from './componentOverrides';
import { ITorqTheme } from './torqTypes';

// Acento do menu lateral (spec "Menu Lateral — TORQ") — cor própria, só
// desse componente, não o `brand.primary` global (usado em botões/chips
// pela aplicação inteira) para não sair alterando a marca inteira por uma
// mudança pedida especificamente pro menu.
const menuAccent = '#00E2F4';

// Paleta TORQ (spec "Menu Lateral — TORQ") — sidebar em navy escuro com
// acento ciano. Não deriva de `lightColors` (pensadas para superfícies
// claras) porque a sidebar continua escura mesmo com o conteúdo da página
// claro.
const darkMenuSidebar = {
  bg: '#001E27',
  // Levemente mais clara que `bg` — mesmo tom usado no hover geral — só pra
  // destacar sutilmente a faixa do logo, sem introduzir uma terceira cor.
  logoAreaBg: '#011F29',
  border: '#1E2A36',
  textPrimary: '#FFFFFF',
  // Cor dos itens do menu (não ativos) — mesma pros ícones, ver `iconDefault`
  // abaixo (a spec diferencia texto/ícone só quando o item está ativo).
  textSecondary: '#CBD5E1',
  // Hover do item: tingimento sutil na cor de acento (6% de opacidade), não
  // mais um navy sólido — o ícone/texto também reagem no hover, ver
  // SidebarItem.tsx.
  hover: 'rgba(0, 226, 244, 0.06)',
  scrollbarThumb: 'rgba(255, 255, 255, 0.18)',
  // Item ativo: fundo TRANSPARENTE — só a barra de acento lateral (mais
  // larga, tipo "gota") e a cor do ícone marcam a seleção; o texto continua
  // branco igual aos itens normais, sem um azul forte "gritando" na letra.
  activeGradient: 'transparent',
  activeAccentBar: menuAccent,
  activeText: '#FFFFFF',
  iconDefault: '#94A3B8',
  iconActive: menuAccent,
  // Submenu que expande pra direita — mesmo acento do item ativo, pra
  // "selecionado" significar sempre a mesma cor em qualquer nível do menu.
  submenuBg: '#012D3D',
  submenuHover: '#073C46',
  submenuSelectedBg: 'rgba(0, 226, 244, 0.18)',
  submenuTextPrimary: '#E6F7F8',
  submenuIconDefault: '#7DD3E8',
  submenuIconSelected: menuAccent,
};

// Variante de sidebar clara — mesmas superfícies do conteúdo da página.
const lightMenuSidebar = {
  bg: lightColors.paper,
  logoAreaBg: lightColors.level2,
  border: lightColors.borderDefault,
  // Navy fixo da marca (o mesmo tom usado como FUNDO da sidebar escura) em
  // vez do cinza genérico de `lightColors` — reforça a identidade do menu
  // mesmo na variante clara. `textSecondary` usa o mesmo valor (não um tom
  // mais claro) de propósito: item ativo e inativo precisam ficar com a
  // MESMA cor de letra/ícone, já que só o fundo + barra de acento (abaixo)
  // marcam qual está selecionado — sem isso, o item ativo destoava com um
  // azul forte na própria letra, difícil de ler e inconsistente com o resto.
  textPrimary: brand.menuBase,
  textSecondary: brand.menuBase,
  hover: lightColors.hover,
  scrollbarThumb: lightColors.scrollbarThumb,
  activeGradient: lightColors.selected,
  activeAccentBar: brand.primary,
  activeText: brand.menuBase,
  // Mesmo raciocínio do texto: ícone não muda de cor entre ativo/inativo
  // nessa variante, só o fundo + barra de acento marcam a seleção.
  iconDefault: brand.menuBase,
  iconActive: brand.menuBase,
  // Submenu segue o mesmo raciocínio: mesma cor de letra/ícone selecionado
  // ou não, sem o azul forte de antes.
  submenuBg: lightColors.card,
  submenuHover: lightColors.hover,
  submenuSelectedBg: lightColors.selected,
  submenuTextPrimary: brand.menuBase,
  submenuIconDefault: brand.menuBase,
  submenuIconSelected: brand.menuBase,
};

const buildLightTorq = (sidebar: typeof darkMenuSidebar): ITorqTheme => ({
  surface: {
    page: lightColors.page,
    level2: lightColors.level2,
    card: lightColors.card,
    elevated: lightColors.elevated,
  },
  border: {
    default: lightColors.borderDefault,
    strong: lightColors.borderStrong,
    focus: lightColors.borderFocus,
  },
  text: {
    primary: lightColors.textPrimary,
    secondary: lightColors.textSecondary,
    muted: lightColors.textMuted,
    disabled: lightColors.textDisabled,
  },
  hover: lightColors.hover,
  selected: lightColors.selected,
  scrollbarThumb: lightColors.scrollbarThumb,
  scrollbarThumbHover: lightColors.scrollbarThumbHover,
  dialogShadow: lightColors.dialogShadow,
  iconBadge: iconBadgeTones,
  sidebar: { ...sidebar, divider: sidebar.border },
});

const torq = buildLightTorq(darkMenuSidebar);
const torqLightMenu = buildLightTorq(lightMenuSidebar);

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  // contrastText #002233 (não branco): o botão primário tem fundo
  // `brand.primary`, um azul claro — texto branco em cima dele não tem
  // contraste suficiente (guia seção 3, "Primário: Texto #002233").
  primary: { main: brand.primary, dark: brand.primaryHover, light: brand.primaryLight, contrastText: brand.menuBase },
  secondary: { main: brand.cyan, dark: '#0891B2', light: '#67E8F9', contrastText: '#052E33' },
  error: { main: brand.errorButton, dark: '#BE123C', light: brand.error, contrastText: '#FFFFFF' },
  warning: { main: brand.warningButton, dark: '#B45309', light: brand.warning, contrastText: '#FFFFFF' },
  success: { main: brand.successButton, dark: '#047857', light: brand.success, contrastText: '#FFFFFF' },
  // Mantido num azul separado do `primary` — `brand.primaryLight` é claro
  // demais pra servir de cor de status "info".
  info: { main: '#3B82F6', dark: '#2563EB', light: '#93C5FD', contrastText: '#FFFFFF' },

  background: {
    paper: lightColors.paper,
    default: lightColors.page,
  },

  text: {
    primary: lightColors.textPrimary,
    secondary: lightColors.textSecondary,
    disabled: lightColors.textDisabled,
  },

  divider: lightColors.divider,
};

const lightTypography: ThemeOptions['typography'] = {
  fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',

  // Letter-spacing levemente negativo nos títulos maiores — é o que dá
  // aquele acabamento "apertado"/editorial de dashboard premium (Linear,
  // Vercel etc.), em vez do espaçamento parado do padrão do navegador.
  h1: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '2.5rem', letterSpacing: '-0.02em' },
  h2: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '2rem', letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '1.875rem', letterSpacing: '-0.015em' },
  h4: { fontWeight: 700, color: lightColors.textPrimary, fontSize: '1.5rem', letterSpacing: '-0.01em' },
  h5: { fontWeight: 650, color: lightColors.textPrimary, fontSize: '1.375rem' },
  h6: { fontWeight: 600, color: lightColors.textPrimary, fontSize: '1rem' },

  subtitle1: { color: lightColors.textSecondary },
  subtitle2: { color: lightColors.textMuted },

  body1: { color: lightColors.textPrimary, lineHeight: 1.6, fontSize: '0.875rem' },
  body2: { color: lightColors.textSecondary, lineHeight: 1.5, fontSize: '0.8125rem' },

  caption: { color: lightColors.textMuted, fontSize: '0.75rem' },
  overline: { color: lightColors.textMuted },

  button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
};

// `components` não depende de `torq.sidebar`, então o mesmo resultado serve
// para as duas variantes do tema claro.
const lightComponents = buildComponents('light', torq);
const lightShadows = buildShadows('light');

// Tema claro padrão — sidebar escura (quase preta), como era antes.
export const lightThemeOptions: ThemeOptions = {
  palette: lightPalette,
  typography: lightTypography,
  shape: { borderRadius: radii.md },
  torq,
  components: lightComponents,
  shadows: lightShadows,
};

// Variante do tema claro com a sidebar clara (mesmas superfícies do
// conteúdo). Selecionável junto com o tema pelo usuário (ver accentColors.ts).
export const lightWhiteMenuThemeOptions: ThemeOptions = {
  palette: lightPalette,
  typography: lightTypography,
  shape: { borderRadius: radii.md },
  torq: torqLightMenu,
  components: lightComponents,
  shadows: lightShadows,
};
